import CircularProgress from "@mui/material/CircularProgress";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction, {
  SpeedDialActionProps,
} from "@mui/material/SpeedDialAction";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import { useTheme } from "@mui/styles";
import { getKey } from "core/model/utils/strings";
import { newThemeColors } from "ds_legacy/materials/colors";
import { dp, sizing } from "ds_legacy/materials/metrics";
import { useEffect, useRef, useState } from "react";

export type SpeedDialAction = Pick<
  SpeedDialActionProps,
  "icon" | "tooltipTitle"
> & {
  disabled?: boolean;
  isLoading?: boolean;
  onClick: () => void;
  testId?: string;
};

type SpeedDialProps = {
  actions: Array<SpeedDialAction>;
  isInitiallyOpen?: boolean;
  testId?: string;
  tooltip: string;
};

export default function SpeedDialInput({
  actions,
  isInitiallyOpen = false,
  testId,
  tooltip,
}: SpeedDialProps) {
  const theme = useTheme();
  const [open, setOpen] = useState(isInitiallyOpen);
  const onClick = () => setOpen(!open);
  const isAnyActionLoading = actions.some(({ isLoading }) => isLoading);
  const wasAnyActionLoading = useRef(false);

  useEffect(() => {
    if (wasAnyActionLoading.current && !isAnyActionLoading) {
      setOpen(false);
    }
    wasAnyActionLoading.current = isAnyActionLoading;
  }, [isAnyActionLoading]);

  return (
    <div style={{ height: open ? undefined : sizing(6) }}>
      <SpeedDial
        ariaLabel={tooltip}
        direction="down"
        FabProps={{
          sx: {
            "&:hover": {
              backgroundColor: theme.palette.secondary.dark,
            },
            backgroundColor: theme.palette.secondary.dark,
            color: newThemeColors.gray[900],
            height: dp(40),
            margin: "8px",
            width: dp(40),
          },
          ...{ "data-testid": testId },
        }}
        icon={<SpeedDialIcon />}
        open={open}
        onClick={onClick}
      >
        {actions.map(
          (
            { disabled, icon, isLoading, onClick, testId, tooltipTitle },
            index,
          ) => {
            const isDisabled = disabled || isLoading;
            const showsLoadingState = isLoading !== undefined;
            return (
              <SpeedDialAction
                key={getKey(index)}
                FabProps={{
                  disabled: isDisabled,
                  sx: {},
                  ...{ "data-testid": testId },
                }}
                icon={
                  isLoading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : (
                    icon
                  )
                }
                sx={{
                  "& .MuiSpeedDialAction-staticTooltipLabel": {
                    width: sizing(26),
                  },
                }}
                tooltipOpen
                tooltipTitle={tooltipTitle}
                onClick={(event) => {
                  if (isDisabled) {
                    event.stopPropagation();
                    return;
                  }
                  onClick();
                  if (showsLoadingState) {
                    // keep the menu open so the loading state stays visible
                    event.stopPropagation();
                  } else {
                    setOpen(false);
                  }
                }}
              />
            );
          },
        )}
      </SpeedDial>
    </div>
  );
}
